/**
 * Печатная форма листа назначений.
 *
 * Отдельный компонент: на экране невидим (display: none), показывается
 * только в режиме печати — когда на body висят классы
 * print-only-profile + print-profile (их ставит handlePrint родителя).
 * Вся логика «что печатать» (шапка с пациентом, таблица приёмов,
 * отметки о выполнении) живёт здесь, чтобы не путать её
 * с интерактивным экраном.
 */
import React from "react";
import { useTranslation } from "react-i18next";
import { getGroupTypeLabel } from "../../../i18n/groupTypes";
import LogoText from "../../../media/60njktpitzg8gano2erfouonjejsbaz5.png";
import logoImg from "../../../media/icon_refresh.png";
import "./patientAppointmentsPrint.css";

// Форматирование даты dd.mm.YYYY; пустые/нулевые даты — прочерк.
const formatDateShort = (dateString) => {
  if (
    !dateString ||
    dateString === "0000-00-00 00:00" ||
    dateString === "0000-00-00 00:00:00"
  ) {
    return "—";
  }
  const [datePart] = String(dateString).split(" ");
  const [year, month, day] = datePart.split("-");
  if (!year || !month || !day) return "—";
  return `${day}.${month}.${year}`;
};

// Дата для шапки столбца — dd.mm
const formatDateDayMonth = (dateString) => {
  if (
    !dateString ||
    dateString === "0000-00-00 00:00" ||
    dateString === "0000-00-00 00:00:00"
  ) {
    return "—";
  }
  const [datePart] = String(dateString).split(" ");
  const [year, month, day] = datePart.split("-");
  if (!year || !month || !day) return "—";
  return `${day}.${month}`;
};

const isValidDate = (value) =>
  Boolean(value) &&
  value !== "0000-00-00 00:00" &&
  value !== "0000-00-00 00:00:00" &&
  !Number.isNaN(new Date(value).getTime());

// Первая плановая дата приёма, иначе первая фактическая.
const getFirstPlanOrActualDate = (admissions) => {
  if (!admissions) return null;
  let firstPlanDate = null;
  let firstActualDate = null;

  Object.values(admissions).forEach((admission) => {
    const plan = admission?.date_plan?.value;
    const actual = admission?.date?.value;
    if (isValidDate(plan) && (!firstPlanDate || new Date(plan) < new Date(firstPlanDate))) {
      firstPlanDate = plan;
    }
    if (
      isValidDate(actual) &&
      (!firstActualDate || new Date(actual) < new Date(firstActualDate))
    ) {
      firstActualDate = actual;
    }
  });

  if (!firstPlanDate && !firstActualDate) return null;
  if (!firstPlanDate) return firstActualDate;
  if (!firstActualDate) return firstPlanDate;
  return new Date(firstPlanDate) < new Date(firstActualDate)
    ? firstPlanDate
    : firstActualDate;
};

const findAppointment = (appointments, number) =>
  (appointments || []).find(
    (appointment) =>
      parseInt(appointment?.appointment_number?.value, 10) === number,
  );

function PatientAppointmentsPrint({ patient, group, appointmentNumbers }) {
  const { t } = useTranslation("patient");
  const result = patient?.result || {};
  const admissions = result.admission || {};
  const items = group?.items || [];

  // Информация о приёме по его номеру (первая услуга с привязанным приёмом).
  const getAdmissionInfo = (number) => {
    for (const item of items) {
      const appointment = findAppointment(item.appointments, number);
      const admissionId = appointment?.id_admission?.value;
      if (admissionId && admissionId !== "0") {
        return admissions[admissionId] || null;
      }
    }
    return null;
  };

  // filled — приём состоялся, scheduled — назначен, но не отмечен.
  const getStatus = (appointments, number) => {
    const appointment = findAppointment(appointments, number);
    if (!appointment) return null;
    if (appointment?.id_admission?.value !== "0") {
      const admissionInfo = admissions[appointment.id_admission.value];
      return isValidDate(admissionInfo?.date?.value) ? "filled" : "scheduled";
    }
    return "scheduled";
  };

  return (
    <div className="appt-print">
      {/* Шапка документа: логотип, название, данные пациента. */}
      <div className="appt-print__head">
        <img alt={t("appointments.logoAlt")} className="appt-print__logo" src={LogoText} />
        <div className="appt-print__doc-title">{t("appointments.printTitle")}</div>
      </div>

      <div className="appt-print__info">
        <div className="appt-print__info-col">
          <div>{t("appointments.printFio", { name: result.fio?.value ?? "—" })}</div>
          <div>
            {t("appointments.printBirthDate")}
            {formatDateShort(result.birthDate?.value)}
          </div>
        </div>
        <div className="appt-print__info-col">
          <div>{t("appointments.printCart", { cart: result.cart_patient?.value ?? "—" })}</div>
          <div>
            {t("appointments.printTreatmentStart")}
            {formatDateShort(getFirstPlanOrActualDate(admissions))}
          </div>
        </div>
        <div className="appt-print__info-col">
          <div>{t("appointments.printDoctor", { name: result.doc_name?.value ?? "—" })}</div>
          <div>{t("appointments.printSignature")}</div>
          <div>{t("appointments.printSpecialMarks")}</div>
        </div>
      </div>

      <div className="appt-print__info-second">
        <div>
          <div>{t("appointments.printDs")}</div>
          <div>{t("appointments.printAllergyHistory")}</div>
        </div>
        <img alt={t("appointments.imageAlt")} className="appt-print__mark" src={logoImg} />
      </div>

      {/* Таблица назначений: услуги × приёмы. */}
      <table className="appt-print__table">
        <thead>
          <tr>
            <th className="appt-print__service-col">{getGroupTypeLabel(t, group)}</th>
            {appointmentNumbers.map((number) => (
              <th key={number} className="appt-print__appointment-col">
                <div>{t("appointments.colAppointment", { number })}</div>
                <div className="appt-print__appointment-date">
                  {formatDateDayMonth(getAdmissionInfo(number)?.date?.value)}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr key={item?.id?.value}>
              <td className="appt-print__service-col">
                <div className="appt-print__service-title">{item?.name?.value}</div>
                {item?.composition?.value ? (
                  <div className="appt-print__service-note">{item.composition.value}</div>
                ) : null}
                {(item?.subitems || []).map((sub) => (
                  <div className="appt-print__service-note" key={sub?.id?.value}>
                    — {sub?.name?.value}
                    {sub?.composition?.value ? ` (${sub.composition.value})` : ""}
                  </div>
                ))}
                {item?.technology?.value ? (
                  <div className="appt-print__service-note">{item.technology.value}</div>
                ) : null}
              </td>
              {appointmentNumbers.map((number) => (
                <td key={number} className="appt-print__cell">
                  {(() => {
                    const status = getStatus(item.appointments, number);
                    if (!status) return null;
                    return <span className={`appt-print__dot ${status}`} />;
                  })()}
                </td>
              ))}
            </tr>
          ))}

          <tr className="appt-print__extra-row">
            <td className="appt-print__service-col">{t("appointments.rowAllergics")}</td>
            {appointmentNumbers.map((number) => (
              <td key={number} className="appt-print__cell" />
            ))}
          </tr>

          <tr className="appt-print__extra-row">
            <td className="appt-print__service-col">{t("appointments.rowPressure")}</td>
            {appointmentNumbers.map((number) => (
              <td key={number} className="appt-print__cell">
                {getAdmissionInfo(number)?.pressure?.value?.trim() || "—"}
              </td>
            ))}
          </tr>

          <tr className="appt-print__extra-row">
            <td className="appt-print__service-col">{t("appointments.rowNurse")}</td>
            {appointmentNumbers.map((number) => (
              <td key={number} className="appt-print__cell" />
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  );
}

export default PatientAppointmentsPrint;
